import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { LANGUAGE_OPTIONS } from "../../../i18n/languages";
import QuizQuestion from "./QuizQuestion";
import useQuizSession, { DUMMY_QUIZ } from "./useQuizSession";
import "./QuizModal.css";

const QuizModal = ({
  isOpen,
  onClose,
  onComplete,
  quiz = DUMMY_QUIZ,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const questions = quiz?.questions || [];
  const [languageCode, setLanguageCode] = useState(
    () => i18n.resolvedLanguage || i18n.language || LANGUAGE_OPTIONS[0].code,
  );
  const [showAnswerRequired, setShowAnswerRequired] = useState(false);

  const {
    currentIndex,
    total,
    currentQuestion,
    selectedOptionId,
    isChecked,
    canCheck,
    canGoPrevious,
    canGoNext,
    isLastQuestion,
    optionState,
    score,
    selectOption,
    checkAnswer,
    goToPrevious,
    goToNext,
    resetSession,
  } = useQuizSession(questions);

  const hasAnswered = Boolean(selectedOptionId);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      resetSession();
      setLanguageCode(
        i18n.resolvedLanguage || i18n.language || LANGUAGE_OPTIONS[0].code,
      );
      setShowAnswerRequired(false);
    }
  }, [isOpen, resetSession, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    setShowAnswerRequired(false);
  }, [currentIndex]);

  if (!isOpen || typeof document === "undefined") return null;

  const progressLabel = t("readingAssistant.quiz.progress", {
    current: total ? currentIndex + 1 : 0,
    total,
  });

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleSelectOption = (optionId) => {
    selectOption(optionId);
    setShowAnswerRequired(false);
  };

  const handleNextOrFinish = () => {
    if (!hasAnswered) {
      setShowAnswerRequired(true);
      return;
    }

    setShowAnswerRequired(false);

    if (isLastQuestion) {
      onComplete?.(score);
      onClose();
      return;
    }

    if (canGoNext) goToNext();
  };

  return createPortal(
    <div
      className="ai-quiz-overlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="ai-quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-quiz-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="ai-quiz-header">
          <div className="ai-quiz-header-text">
            <h2 id="ai-quiz-title">{t("readingAssistant.quiz.modalTitle")}</h2>
            <p className="ai-quiz-disclaimer">
              {t("readingAssistant.quiz.disclaimer")}
            </p>
          </div>
          <button
            type="button"
            className="ai-quiz-close"
            onClick={onClose}
            aria-label={t("readingAssistant.quiz.close")}
            title={t("readingAssistant.quiz.close")}
          >
            <CloseIcon width={20} height={20} aria-hidden="true" />
          </button>
        </header>

        <QuizQuestion
          question={currentQuestion}
          optionState={optionState}
          onSelectOption={handleSelectOption}
          isChecked={isChecked}
          languageCode={languageCode}
          onLanguageChange={setLanguageCode}
          languageLabel={t("common.language")}
          canCheck={canCheck}
          onCheckAnswer={checkAnswer}
          checkLabel={t("readingAssistant.quiz.checkAnswer")}
          explanationLabel={t("readingAssistant.quiz.explanation")}
          progressLabel={isMobile ? progressLabel : null}
          answerOptionsLabel={t("readingAssistant.quiz.answerOptions")}
        />

        <footer className="ai-quiz-footer">
          <button
            type="button"
            className="ai-quiz-nav ai-quiz-nav--secondary"
            onClick={goToPrevious}
            disabled={!canGoPrevious}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>{t("readingAssistant.quiz.previous")}</span>
          </button>

          <div className="ai-quiz-footer-center">
            {!isMobile && (
              <p className="ai-quiz-progress">{progressLabel}</p>
            )}
            {showAnswerRequired && (
              <p className="ai-quiz-nav-error" role="alert">
                {t("readingAssistant.quiz.answerRequired")}
              </p>
            )}
          </div>

          <button
            type="button"
            className="ai-quiz-nav ai-quiz-nav--primary"
            onClick={handleNextOrFinish}
            disabled={!isLastQuestion && !canGoNext && hasAnswered}
          >
            <span>
              {isLastQuestion
                ? t("readingAssistant.quiz.finish")
                : t("readingAssistant.quiz.next")}
            </span>
            {!isLastQuestion && <FaChevronRight aria-hidden="true" />}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default QuizModal;
